/**
 * LRLC — reservoir beresistivitas rendah dan berkontras rendah.
 *
 * Satu layar untuk satu pertanyaan: *di mana log ini berbohong*. Sebuah
 * reservoir berhidrokarbon biasanya menonjol karena resistivitasnya tinggi,
 * dan penafsiran resistivitas berdiri di atas kenyataan itu sejak Archie.
 * Sebagian reservoir tidak menurut: lapisan tipis berselang-seling, mineral
 * lempung penghantar, dan butiran halus yang menahan air terikat menahan RT
 * tetap rendah meskipun porinya berisi minyak. Yang terbaca adalah lapisan
 * yang tampak berair — dan sumur yang ditinggalkan karenanya adalah sumur yang
 * meninggalkan minyaknya di dalam tanah.
 *
 * Tiga hal yang dipisahkan dengan sengaja.
 *
 * **Menandai dan membuktikan adalah dua pekerjaan.** Kurva bisa mengatakan
 * sebuah lapisan *berperilaku* seperti LRLC. Hanya uji produksi yang bisa
 * mengatakan ia memang berisi minyak. Keduanya karena itu memulangkan kelas
 * yang berbeda, dan panel uji produksi berdiri di rel yang sama supaya
 * keduanya tidak pernah terpisah.
 *
 * **Yang membantah ikut dipajang.** Kelima perbandingan dihitung, bukan satu:
 * RT/RO bergeser bersama Rw yang keliru, dan NPHI–RHOB tidak membaca
 * resistivitas sama sekali. Sebuah layar yang memajang satu angka adalah layar
 * yang meyakinkan.
 *
 * **Rw disebutkan, tidak disembunyikan.** Ia menggeser RO, dan RO adalah garis
 * yang dibandingkan dengan RT. Rw yang meleset dua kali lipat memindahkan
 * seluruh sumur dari "air" ke "membayar", dan tidak ada apa pun pada gambarnya
 * yang memperlihatkan itu.
 */

import { useMemo, useState } from "react";
import { api } from "../api";
import { canCompute } from "../auth";
import {
  Button,
  Checkbox,
  Dropdown,
  Empty,
  KeyValue,
  Loading,
  Notice,
  NumberField,
  Panel,
  Segmented,
  useToast,
} from "../components/ui";
import { useAsync, useWellSync } from "../hooks";
import { colorOf, lrlcThickness, provenHint } from "../lib/lrlc";
import { count } from "../lib/plural";
import { num } from "../lib/rocktype";
import type {
  LrlcCatalogue,
  LrlcClassRow,
  LrlcPayload,
  Project,
  Well,
} from "../types";
import { useSearchParams } from "react-router-dom";
import ProductionTests from "../components/ProductionTests";
import LrlcCrossplot from "../components/LrlcCrossplot";
import LrlcEstimate from "../components/LrlcEstimate";
import LrlcLogSheet, { type LrlcSheetRequest } from "../components/LrlcLogSheet";
import LrlcRwCrossplot, { type LrlcRwRequest } from "../components/LrlcRwCrossplot";
import {
  DEFAULT_RW_SCOPE,
  rwScopeBody,
  type RwFollowing,
  type RwScope,
} from "../components/LrlcRwScope";

/**
 * Kurva yang dibaca, dalam urutan yang dibaca orang.
 *
 * Tiga di antaranya boleh tidak dipilih, dan itu bukan kelonggaran melainkan
 * bentuk data yang sebenarnya. Yang melahirkan jawaban layar ini hanyalah RT,
 * VSH dan PHIE — F dari PHIE, RO dari F, RT terhadap RO, dan VSH yang
 * memutuskan sebuah lapisan reservoir sama sekali. RHOB dan NPHI dibaca oleh
 * satu aturan saja, batubara, dan PHIT tidak dibaca penggolong sama sekali: ia
 * berdiri di lembar log di sebelah PHIE, dan jarak keduanya adalah air terikat
 * lempung.
 *
 * Sampai 20 September 2026 ketiganya dituntut sekeras ketiga yang pertama, dan
 * akibatnya sumur tua yang hanya membawa GR, resistivitas dan sonik ditolak
 * seluruhnya — bukan "tanpa kolom batubara" melainkan "tidak bisa
 * digolongkan". Lapangan yang dilog puluhan tahun memuat keduanya di satu
 * proyek yang sama, dan menuntut rangkaian lengkap pada tiap sumurnya berarti
 * layar ini hanya bisa dipakai pada sumur yang paling tidak memerlukannya.
 */
const READS: { key: string; label: string; optional?: boolean }[] = [
  { key: "rt", label: "Deep resistivity" },
  { key: "rhob", label: "Bulk density", optional: true },
  { key: "nphi", label: "Neutron porosity", optional: true },
  { key: "vsh", label: "Shale volume" },
  { key: "phit", label: "Total porosity", optional: true },
  { key: "phie", label: "Effective porosity" },
];

/** Ketiga kurva yang tidak bisa ditinggalkan. */
const REQUIRED = READS.filter((read) => !read.optional);

/** Ambang yang hanya berarti kalau densitas **dan** neutron sama-sama dibaca. */
const COAL_CUTOFFS = ["rhob_cutoff", "nphi_cutoff"];

/** Ambang yang boleh digeser, berikut satuan dan keterangannya. */
const CUTOFFS: { key: string; label: string; step: number; hint: string }[] = [
  { key: "rt_ro_cutoff", label: "RT / RO at least", step: 0.1,
    hint: "Above this, something is holding RT above its water line." },
  { key: "low_res_rt", label: "Low resistivity below", step: 0.5,
    hint: "Pay below this reads as low resistivity; above it, only low contrast." },
  { key: "rt_rw_cutoff", label: "RT / Rw at least", step: 1,
    hint: "Separates low contrast from an ordinary pay zone." },
  { key: "vsh_max", label: "VSH at most", step: 0.05,
    hint: "Cleaner than this to count as reservoir at all." },
  { key: "phie_min", label: "PHIE at least", step: 0.01, hint: "" },
  { key: "rhob_cutoff", label: "Coal: RHOB below", step: 0.05,
    hint: "Coal mimics every sign looked for, so it is decided first." },
  { key: "nphi_cutoff", label: "Coal: NPHI above", step: 0.05, hint: "" },
  { key: "smooth_m", label: "Smooth below", step: 0.1,
    hint: "Blocks thinner than this are absorbed by their neighbours." },
];

type Tab = "classes" | "intervals" | "wells" | "tests" | "rw";

/**
 * Tab yang boleh disebut di alamatnya, dalam urutan yang dipajang.
 *
 * Rw & RO berdiri paling kiri, dan itu urutan pekerjaannya sendiri. Rw
 * menggeser RO, RO adalah garis yang dibandingkan dengan RT, dan RT/RO-lah
 * yang memisahkan air dari yang membayar — jadi Rw dibaca **sebelum**
 * penggolongan dijalankan, bukan sesudahnya. Selama ia duduk di ujung kanan
 * deret, ia terbaca sebagai lampiran atas hasil, dan yang membacanya begitu
 * menjalankan penggolongnya dengan Rw yang belum pernah diperiksa.
 *
 * Yang terpilih tanpa `?tab=` tetap Classes: urutan membaca dan tempat
 * mendarat adalah dua hal, dan layar yang mendarat di sebuah crossplot ketika
 * yang diminta orang adalah tabel hasilnya memindahkan satu klik ke setiap
 * kunjungan berikutnya.
 */
const TABS: Tab[] = ["rw", "classes", "intervals", "wells", "tests"];

/**
 * Tab yang berdiri tanpa hasil penggolongan.
 *
 * Ketiga tab lain adalah tabel atas jawaban penggolong: tanpa jawaban tidak
 * ada barisnya. Kedua yang di bawah bukan. Uji produksi adalah data yang
 * dimasukkan orang tentang sumurnya, dan crossplot Rw menghitung sendiri dari
 * ambang yang sedang terpasang — keduanya tidak punya alasan menunggu tombol
 * ditekan lebih dulu. Rw bahkan sebaliknya: ia justru dibaca **sebelum**
 * penggolongan dijalankan, sebab Rw-lah yang menggeser RO.
 */
const STANDALONE: Tab[] = ["tests", "rw"];

export default function Lrlc({
  project,
  well,
  wells,
  onWellChange,
}: {
  project: Project;
  /** Sumur yang sedang aktif di cangkang, atau null pada proyek tanpa sumur. */
  well?: Well | null;
  wells: Well[];
  /**
   * Dipanggil ketika lembar log layar ini berpindah sumur, supaya pemilih WELL
   * di cangkang menunjuk sumur yang sama. Lihat `useWellSync`.
   */
  onWellChange?: (wellId: number) => void;
}) {
  const toast = useToast();
  const catalogue = useAsync(
    (signal) => api.get<LrlcCatalogue>(`/api/projects/${project.id}/lrlc/`, undefined, signal),
    [project.id],
  );

  const [picks, setPicks] = useState<Record<string, string>>({});
  const [selection, setSelection] = useState<Set<number> | null>(null);
  const [cutoffs, setCutoffs] = useState<Record<string, number>>({});
  const [archie, setArchie] = useState<Record<string, number | null>>({});
  const [zone, setZone] = useState("");
  /**
   * Lingkup crossplot Rw: sumur mana, dan kedalaman mana.
   *
   * Milik tab Rw dan bukan milik layar, sebab pertanyaannya memang berbeda.
   * Rel kiri memilih sumur yang **digolongkan** — ke sumur itulah kurva LRLC
   * diterbitkan. Rw satu angka milik air formasi, dan yang paling sering benar
   * adalah membacanya dari sumur di sekeliling sumur yang dianalisis, termasuk
   * sumur yang tidak sedang digolongkan sama sekali.
   */
  const [rwScope, setRwScope] = useState<RwScope>(DEFAULT_RW_SCOPE);
  /**
   * Tab dan lembar log tinggal di **alamatnya**, bukan di keadaan React.
   *
   * Dua alasan, dan yang kedua yang memaksanya. Pertama, keduanya hal yang
   * orang kirim ke sebelahnya — "lihat lembar log KTI-09 pada ambang ini" —
   * dan tautan yang memulihkannya hanya ada kalau letaknya tertulis di alamat.
   *
   * Kedua: ketiga butir menu LRLC menavigasi ke rute yang **sama** dengan
   * query yang berbeda, dan React Router tidak memasang ulang komponen untuk
   * itu. Nilai awal `useState` hanya dibaca sekali, pada pemasangan, jadi
   * sebuah salinan alamat ke dalam keadaan membuat butir menunya bekerja tepat
   * ketika layarnya kebetulan belum terbuka dan diam setiap kali sesudahnya —
   * dan diamnya tanpa satu pun galat. Dibaca langsung dari `search`, tidak ada
   * yang bisa berselisih dengan alamatnya sebab tidak ada salinan kedua.
   */
  const [search, setSearch] = useSearchParams();
  const tab: Tab = TABS.includes(search.get("tab") as Tab)
    ? (search.get("tab") as Tab) : "classes";
  /** Menulis satu kunci ke alamat, atau membuangnya bila kosong. */
  const putSearch = (key: string, value: string | null) =>
    setSearch((prev) => {
      const next = new URLSearchParams(prev);
      if (value === null) next.delete(key); else next.set(key, value);
      return next;
    }, { replace: true });
  const setTab = (value: Tab) =>
    putSearch("tab", value === "classes" ? null : value);
  const [result, setResult] = useState<LrlcPayload | null>(null);
  const [running, setRunning] = useState(false);
  const [version, setVersion] = useState(0);
  /**
   * Sumur yang lembar lognya sedang dibuka, atau `null` ketika tertutup.
   *
   * Nomor sumur dan bukan sekadar sebuah bendera: lembar dibuka dari baris
   * sumur di tabel, dan sebuah jendela yang selalu terbuka pada sumur pertama
   * memaksa penggunanya memilih ulang sumur yang barusan ditunjuknya.
   */
  const sheetWell = (() => {
    const asked = Number(search.get("sheet"));
    return Number.isFinite(asked) && asked > 0 ? asked : null;
  })();
  const setSheetWell = (wellId: number | null) =>
    putSearch("sheet", wellId === null ? null : String(wellId));
  /** Jendela Estimate LRLC, di alamatnya seperti lembar log. */
  const estimateOpen = search.get("estimate") === "1";

  /* Lembar log yang terbuka adalah satu-satunya tempat layar ini berdiri di
     atas **satu** sumur. Rel kiri memilih sumur yang digolongkan — biasanya
     seluruh lapangan sekaligus — dan lingkup Rw memilih sumur yang lain lagi;
     tidak satu pun dari keduanya bisa diwakili sebuah pemilih tunggal. Ketika
     lembarnya tertutup, `null` di sini berarti pemilih di cangkang dibiarkan
     pada sumur yang tadi dipegangnya. */
  useWellSync(sheetWell, well?.id ?? null, {
    onFocus: onWellChange,
    onAdopt: setSheetWell,
    // Sumur lembar ini tertulis di alamatnya, jadi pada pemasangan alamat itu
    // yang menang: `?sheet=12` adalah kalimat yang baru saja diucapkan
    // seseorang — sebuah tautan kiriman, atau butir menubar yang barusan
    // diklik — dan bukan keadaan yang dipulihkan dari peramban.
    adoptOnMount: false,
  });

  const available = catalogue.data?.available ?? [];
  const suggested = catalogue.data?.suggested ?? {};
  const classes = result?.classes ?? catalogue.data?.classes ?? [];
  const defaults = catalogue.data?.defaults ?? {};
  const mayPublish = canCompute(project.my_role);

  /** Sumur yang benar-benar bisa dihitung: yang belum dibor tidak punya kurva. */
  const drillable = useMemo(
    () => (catalogue.data?.wells ?? []).filter((w) => !w.undrilled),
    [catalogue.data],
  );
  const chosen = selection ?? new Set(drillable.map((w) => w.id));
  /** Lapangan tiap sumur, dari daftar sumur cangkang; katalog LRLC tidak memuatnya. */
  const fieldOf = useMemo(() => new Map(wells.map((w) => [w.id, w.field ?? ""])), [wells]);

  /**
   * Kurva yang dipakai untuk sebuah peran — pilihan orangnya, atau tebakan.
   *
   * `key in picks` dan bukan `picks[key] ||`: sejak ketiga kurva di bawah
   * boleh tidak dipakai, "" adalah sebuah **pilihan** — "Not used" — dan bukan
   * sekadar kotak yang belum disentuh. Yang membacanya dengan `||` akan
   * memulangkan tebakan katalog kepada orang yang baru saja mematikannya, dan
   * satu-satunya tanda di layar adalah kolom yang tetap tergambar.
   */
  const curveOf = (key: string) =>
    (key in picks ? picks[key] : suggested[key]) || "";

  /**
   * Benar ketika batubara tidak bisa ditemukan dari kurva sama sekali.
   *
   * Aturannya menuntut keduanya sekaligus — RHOB di bawah ambangnya **dan**
   * NPHI di atas ambangnya — jadi satu kurva yang tidak dipakai sudah cukup
   * untuk membuat kedua penggeser di bawah tidak mengubah apa pun. Yang masih
   * berlaku adalah kurva litologi, yang menyebut COAL dengan namanya.
   */
  const coalUnread = !curveOf("rhob") || !curveOf("nphi");

  /**
   * Sumur pertama yang tercentang **dan berkurva** — yang dibuka lembar log.
   *
   * Yang berkurva, sebab sebuah proyek boleh memuat sumur yang hanya dikenal
   * tabel penanda: Prabumulih punya 98 di antaranya, dan yang pertama menurut
   * abjad — ABR-001 — tidak punya satu kurva pun. Membuka lembar pada sumur
   * itu memajang "has no active curves yet" sebagai hal pertama yang dilihat
   * orang, padahal lima ratus sumur lain di sebelahnya bisa digambar. Yang
   * tanpa kurva tetap ada di pemilih sumur lembarnya, dan tetap menjawab
   * dengan kalimat itu bila memang dipilih sendiri.
   */
  const firstChosen = useMemo(
    () => (drillable.find((well) => chosen.has(well.id) && well.logged)
           ?? drillable.find((well) => chosen.has(well.id)))?.id ?? null,
    [drillable, chosen],
  );

  const zoneNames = useMemo(() => {
    const names = new Set<string>();
    for (const well of wells) {
      if (!chosen.has(well.id)) continue;
      for (const z of well.zones ?? []) names.add(z.name);
    }
    return [...names].sort();
  }, [wells, chosen]);

  const testedWells = useMemo(
    () => (catalogue.data?.wells ?? [])
      .filter((w) => chosen.has(w.id))
      .reduce((sum, w) => sum + w.tests, 0),
    [catalogue.data, chosen],
  );

  /**
   * Apa yang diminta lembar log, dari setelan yang **sedang terpasang**.
   *
   * Bukan dari `result`. Yang tersimpan di sana adalah jawaban hitungan
   * terakhir, dan lembar yang menggambarnya sambil berdiri di sebelah
   * penggeser yang sudah digeser adalah lembar yang berbohong tanpa satu pun
   * tanda di permukaannya. Lembar ini menghitung ulang sendiri, dan itu murah:
   * satu sumur, bukan seluruh proyek.
   */
  /**
   * Apa yang diminta crossplot Rw. Sama dengan lembar log, ditambah lingkupnya
   * sendiri: Rw dibaca dari kaki air beberapa sumur sekaligus — sebuah sumur
   * dengan tiga puluh sampel kaki air tidak cukup untuk sebuah garis, dan lima
   * sumur seperti itu cukup — dan sumur yang paling sering benar untuk dibaca
   * bukan sumur yang sedang digolongkan melainkan yang **di sekelilingnya**.
   *
   * Yang diikutinya selama belum disentuh ada dua, dan keduanya berbeda
   * derajat. Kedalamannya mengikuti Interval di rel kiri, satu nilai untuk
   * satu layar. Daftar sumurnya mengikuti **satu** nama saja — `firstChosen`,
   * sumur yang juga dibuka lembar log, yaitu sumur yang sedang dibaca orang —
   * dan bukan seluruh centang di rel itu: centang di rel kiri memilih sumur
   * yang digolongkan, dan di Prabumulih itu lima ratus sekaligus. Rw yang
   * dirata-ratakan atas lima ratus sumur adalah satu angka atas belasan
   * akuifer yang berlainan, dan tidak ada yang menanyakannya. Sumur kedua
   * ditambahkan dengan tangan di panelnya, dan daftar rel kiri tetap bisa
   * dipanggil sekaligus lewat pintasan di sana.
   */
  const following: RwFollowing = useMemo(
    () => ({ well: firstChosen, wells: [...chosen], zone }),
    [firstChosen, chosen, zone]);

  const rwRequest: LrlcRwRequest = useMemo(() => ({
    curves: {
      ...Object.fromEntries(READS.map((r) => [r.key, curveOf(r.key)])),
      litho: curveOf("litho"),
    },
    cutoffs,
    archie,
    scope: rwScopeBody(rwScope, following),
  }), [picks, suggested, cutoffs, archie, rwScope, following]);

  const sheetRequest: LrlcSheetRequest = useMemo(() => ({
    curves: {
      ...Object.fromEntries(READS.map((r) => [r.key, curveOf(r.key)])),
      litho: curveOf("litho"),
    },
    cutoffs,
    archie,
    zone,
  }), [picks, suggested, cutoffs, archie, zone]);

  /**
   * Nama sumur yang Rw-nya barusan digeser di dalam lembar log, atau "".
   *
   * Sebuah tanda dan bukan sebuah hitungan ulang. Penggolongan seluruh proyek
   * adalah 1.451 sumur di Prabumulih, dan menjalankannya lagi pada tiap angka
   * yang diketik seseorang di panel satu sumur adalah biaya yang tidak diminta
   * siapa pun. Yang tidak boleh terjadi hanyalah diam: tabel yang tetap
   * memajang tebal lamanya di sebelah lembar yang sudah digambar dengan angka
   * baru adalah dua jawaban yang berselisih, dan tidak ada apa pun di layar
   * yang mengatakan mana yang mana.
   */
  const [waterMoved, setWaterMoved] = useState("");

  const run = async (publish: boolean) => {
    setRunning(true);
    setWaterMoved("");
    try {
      const body: Record<string, unknown> = {
        wells: [...chosen],
        zones: zone ? [zone] : [],
        zone,
        publish,
        ...Object.fromEntries(READS.map((r) => [r.key, curveOf(r.key)])),
        litho: curveOf("litho"),
        ...cutoffs,
      };
      for (const key of ["a", "m", "rw"]) {
        if (archie[key] !== null && archie[key] !== undefined) body[key] = archie[key];
      }
      const data = await api.post<LrlcPayload>(
        `/api/projects/${project.id}/lrlc/`, body);
      setResult(data);
      if (publish) {
        const written = data.published?.wells.length ?? 0;
        toast(`LRLC published to ${count(written, "well")} as the LRLC curve set.`);
      } else {
        const metres = lrlcThickness(data.rows);
        toast(metres > 0
          ? `${metres.toFixed(1)} m flagged as low resistivity, low contrast.`
          : "No low-resistivity pay found with these cutoffs.");
      }
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : String(cause), "error");
    } finally {
      setRunning(false);
    }
  };

  if (catalogue.loading) return <Loading label="Reading the curves…" />;
  if (catalogue.error) return <Empty>LRLC could not be opened: {catalogue.error}</Empty>;

  return (
    <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
      <div
        className="scroll-y"
        style={{
          width: 282, flex: "none", padding: 10,
          display: "flex", flexDirection: "column", gap: 9,
          borderRight: "1px solid var(--line)",
        }}
      >
        <Panel title="Curves read">
          <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
            {READS.map((read) => (
              <Dropdown
                key={read.key}
                label={read.optional ? `${read.label} (optional)` : read.label}
                value={curveOf(read.key)}
                options={[
                  ...(read.optional ? [{ value: "", label: "Not used" }] : []),
                  ...available.map((name) => ({ value: name, label: name })),
                ]}
                onChange={(value) => setPicks((prev) => ({ ...prev, [read.key]: value }))}
              />
            ))}
            <span style={{ font: "400 9.5px/1.5 var(--font-sans)", color: "var(--ink-6)" }}>
              RT, VSH and PHIE are what the answer is built from. Density,
              neutron and total porosity may be left out — and a well that was
              never logged for them is read anyway, rather than refused. What
              goes with them is said plainly: coal is only ever found from the
              two curves that can say it, so without them it is found only
              where a lithology curve names it, and the log sheet leaves out
              the columns that read them.
            </span>
            <Dropdown
              label="Lithology (optional)"
              value={curveOf("litho")}
              options={[{ value: "", label: "Not used" },
                        ...available.map((name) => ({ value: name, label: name }))]}
              onChange={(value) => setPicks((prev) => ({ ...prev, litho: value }))}
            />
            <span style={{ font: "400 9.5px/1.5 var(--font-sans)", color: "var(--ink-6)" }}>
              A lithology curve only ever adds: COAL makes a sample coal even
              where the density is not low, and SS makes it reservoir even where
              VSH says otherwise. What people see in the rock beats what the
              curves imply.
            </span>
            <Dropdown
              label="Interval"
              value={zone}
              options={[{ value: "", label: "Every depth" },
                        ...zoneNames.map((name) => ({ value: name, label: name }))]}
              onChange={setZone}
            />
          </div>
        </Panel>

        <Panel title="Wells" meta={`${chosen.size} of ${drillable.length}`}>
          <div style={{ display: "flex", flexDirection: "column", maxHeight: 170, overflowY: "auto" }}>
            {drillable.map((well) => (
              <Checkbox
                key={well.id}
                checked={chosen.has(well.id)}
                label={well.name}
                dot={well.color || undefined}
                meta={well.tests ? count(well.tests, "test") : "no test"}
                onChange={(on) => {
                  const next = new Set(chosen);
                  on ? next.add(well.id) : next.delete(well.id);
                  setSelection(next);
                }}
              />
            ))}
          </div>
        </Panel>

        <Panel title="Archie & formation water">
          <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
            {([["a", "a (tortuosity)", 0.05], ["m", "m (cementation)", 0.05],
               ["rw", "Rw", 0.005]] as const).map(([key, label, step]) => (
              <NumberField
                key={key}
                label={label}
                value={archie[key] ?? Number(defaults[key] ?? 0)}
                step={step}
                onChange={(value) => setArchie((prev) => ({ ...prev, [key]: value }))}
              />
            ))}
            <span style={{ font: "400 9.5px/1.5 var(--font-sans)", color: "var(--ink-6)" }}>
              Blank follows the project parameter set. Rw moves RO, and RO is
              the line RT is compared against — an Rw wrong by a factor of two
              moves a whole well from water to pay, and nothing on the plot
              shows it.
            </span>
          </div>
        </Panel>

        <Panel title="Cutoffs">
          <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
            {CUTOFFS.map((cutoff) => (
              <div key={cutoff.key} style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <NumberField
                  label={cutoff.label}
                  value={cutoffs[cutoff.key] ?? Number(defaults[cutoff.key] ?? 0)}
                  step={cutoff.step}
                  onChange={(value) =>
                    setCutoffs((prev) => ({ ...prev, [cutoff.key]: value }))}
                />
                {/* Ambang batubara yang tidak punya kurva untuk dibandingkan
                    mengatakannya sendiri. Sebuah kotak yang bisa diketik dan
                    tidak mengubah apa pun adalah penggeser yang rusak, dan
                    yang menggesernya akan mencari sebabnya pada hasilnya. */}
                {COAL_CUTOFFS.includes(cutoff.key) && coalUnread ? (
                  <span style={{ font: "400 9px/1.45 var(--font-sans)", color: "var(--amber)" }}>
                    Reads nothing here: coal needs both the density and the
                    neutron curve, and one of them is not in use. What a
                    lithology curve calls COAL is still coal.
                  </span>
                ) : cutoff.hint ? (
                  <span style={{ font: "400 9px/1.45 var(--font-sans)", color: "var(--ink-6)" }}>
                    {cutoff.hint}
                  </span>
                ) : null}
              </div>
            ))}
          </div>
        </Panel>
      </div>

      <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0, padding: 10, gap: 9 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 9, flex: "none" }}>
          <Button variant="primary" onClick={() => run(false)} busy={running}
                  disabled={chosen.size === 0 || REQUIRED.some((r) => !curveOf(r.key))}>
            Flag the low-resistivity pay
          </Button>
          <Button
            variant="ghost"
            onClick={() => setSheetWell(firstChosen)}
            disabled={!firstChosen || REQUIRED.some((r) => !curveOf(r.key))}
            title={firstChosen
              ? "Draw one well as a log sheet — every quantity this screen computes, "
                + "column by column, at the cutoffs now set"
              : "Pick at least one well that has been drilled"}
          >
            Log sheet
          </Button>
          {/* Estimasi dengan machine learning berdiri sesudah lembar log dan
              sebelum Publish: ia belajar dari LRLC_CLASS yang sudah
              diterbitkan, dan menulis taksirannya sendiri — LRLC_CLASS_EST —
              tanpa menyentuh hasil penggolong. */}
          <Button
            variant="ghost"
            onClick={() => putSearch("estimate", "1")}
            disabled={drillable.length < 2}
            title={"Train XGBoost, random forest, SVM or ANN on the wells already "
              + "classified, test it on blind wells, and estimate LRLC elsewhere"}
          >
            Estimate LRLC
          </Button>
          <Button
            variant="ghost"
            onClick={() => run(true)}
            disabled={running || !result || !mayPublish}
            title={mayPublish
              ? "Write LRLC_CLASS, F, RWA, RO and RT/RO into every well"
              : "Your role on this project may not publish curves"}
          >
            Publish as curves
          </Button>
          <span style={{ flex: 1 }} />
          {result && (
            <span style={{ font: "500 11px var(--font-mono)", color: "var(--accent)" }}>
              {lrlcThickness(result.rows).toFixed(1)} m flagged
            </span>
          )}
        </div>

        {result && <LrlcCrossplot payload={result} />}

        {/* Kelima tab berdiri sejak layarnya dibuka, sebelum apa pun dihitung.
            Dahulu barisnya baru muncul setelah "Flag the low-resistivity pay"
            ditekan, dan itu menutup kedua tab yang justru tidak memerlukannya:
            uji produksi adalah data yang dimasukkan orang tentang sumurnya,
            dan Rw dibaca **sebelum** penggolongan sebab Rw-lah yang menggeser
            RO. Keduanya bisa dicapai lewat alamat — `?tab=rw` — tetapi sebuah
            kemampuan yang hanya ada bagi yang sudah tahu alamatnya adalah
            kemampuan yang tidak ada.

            Ketiga tab hasil menerangkan dirinya sendiri di dalam panelnya:
            tanpa jawaban penggolong tidak ada barisnya, dan yang benar untuk
            diperlihatkan di situ adalah jalan keluarnya, bukan tabel kosong. */}
        <Segmented
          value={tab}
          options={[
            { value: "rw", label: "Rw & RO" },
            { value: "classes", label: "Classes" },
            { value: "intervals", label: "Intervals" },
            { value: "wells", label: "Wells" },
            { value: "tests", label: "Production tests" },
          ]}
          onChange={(value) => setTab(value as Tab)}
          size="sm"
        />
        <div className="scroll-y" style={{ flex: 1, minHeight: 120 }}>
            {result && waterMoved && !STANDALONE.includes(tab) && (
              <Notice tone="warn">
                The water {waterMoved} is read with moved after this was
                computed, so the thicknesses below are the ones the old number
                gave. Run it again to bring them level with the sheet.
              </Notice>
            )}
            {!result && running && !STANDALONE.includes(tab) && (
              <Loading label="Reading the resistivity against its water line…" />
            )}
            {!result && !running && !STANDALONE.includes(tab) && (
              <Empty>
                Pick the curves it reads, check the formation water, and run
                it. Nothing is computed until then.
              </Empty>
            )}
            {result && tab === "classes" && (
              <ClassTable payload={result} tests={testedWells} />
            )}
            {result && tab === "intervals" && (
              <IntervalTable payload={result} classes={classes} />
            )}
            {result && tab === "wells" && (
              <WellTable payload={result} onOpenSheet={setSheetWell} />
            )}
            {tab === "rw" && (
              <LrlcRwCrossplot
                project={project}
                request={rwRequest}
                scope={rwScope}
                onScopeChange={setRwScope}
                wells={drillable}
                field={wells}
                following={following}
                onUseRw={(rw) => setArchie((prev) => ({ ...prev, rw }))}
              />
            )}
            {tab === "tests" && (
              <ProductionTests
                project={project}
                wells={wells.filter((w) => chosen.has(w.id))}
                version={version}
                onChanged={() => { setVersion((v) => v + 1); catalogue.reload(); }}
              />
            )}
        </div>
      </div>

      {/* Menunggu daftar sumurnya tiba. `?sheet=12` membuka jendela ini pada
          render pertama, ketika katalog belum mendarat dan `drillable` masih
          kosong — dan jendela yang terbuka tanpa satu pun sumur meminta
          lembar untuk sumur bernomor nol, lalu memperlihatkan galatnya sekejap
          sebelum membetulkan dirinya sendiri. */}
      {estimateOpen && drillable.length > 0 && (
        <LrlcEstimate
          projectId={project.id}
          wells={drillable.map((w) => ({ id: w.id, name: w.name, field: fieldOf.get(w.id) }))}
          available={available}
          classes={classes}
          mayCompute={mayPublish}
          onClose={() => putSearch("estimate", null)}
        />
      )}

      {sheetWell !== null && drillable.length > 0 && (
        <LrlcLogSheet
          project={project}
          wells={drillable.map((w) => ({ id: w.id, name: w.name, color: w.color }))}
          request={sheetRequest}
          wellId={sheetWell}
          // Pemilih di kepala lembar menulis kembali ke alamatnya, dan dari
          // sana `useWellSync` di atas meneruskannya ke cangkang.
          onWellChange={setSheetWell}
          // Rw sebuah sumur barusan berpindah, dan lembarnya sudah menggambar
          // ulang dirinya sendiri. Yang **tidak** ikut adalah tabel di layar
          // ini: keduanya lahir dari penggolongan yang sudah berjalan, dan
          // tebal yang tertulis di sana masih tebal yang dihitung dengan angka
          // yang tidak lagi terpasang di mana pun. Dijalankan ulang sendiri
          // bukan jawabannya — di Prabumulih itu 1.451 sumur atas satu angka
          // yang barusan diketik — jadi yang dilakukan adalah mengatakannya.
          onWaterChanged={setWaterMoved}
          onClose={() => setSheetWell(null)}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ Tabel */

const cell: React.CSSProperties = {
  padding: "5px 9px", textAlign: "right",
  font: "400 10.5px var(--font-mono)",
  borderBottom: "1px solid var(--line-soft)", whiteSpace: "nowrap",
};
const head: React.CSSProperties = {
  ...cell, font: "600 9.5px var(--font-mono)", color: "var(--ink-5)",
  borderBottom: "1px solid var(--line-strong)",
  position: "sticky", top: 0, background: "var(--panel)",
};
const left: React.CSSProperties = { textAlign: "left" };

function Table({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ border: "1px solid var(--line)", borderRadius: 7, overflow: "hidden" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", background: "var(--panel-inset)" }}>
        {children}
      </table>
    </div>
  );
}

function Swatch({ color, label }: { color: string; label: string }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <span style={{ width: 8, height: 8, borderRadius: 2, background: color,
                     border: "1px solid var(--line)", flex: "none" }} />
      {label}
    </span>
  );
}

/** Satu baris per kelas, dan ketebalannya lebih dulu daripada cacah sampelnya. */
function ClassTable({ payload, tests }: { payload: LrlcPayload; tests: number }) {
  const hint = provenHint(payload.rows, tests);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
      {hint && <Notice tone="info">{hint}</Notice>}
      <Table>
        <thead>
          <tr>
            <th style={{ ...head, ...left }}>Class</th>
            <th style={head}>Thickness m</th>
            <th style={head}>Samples</th>
            <th style={head}>Share</th>
            <th style={head}>PHIE mean</th>
            <th style={head}>RT mean</th>
          </tr>
        </thead>
        <tbody>
          {payload.rows.map((row: LrlcClassRow) => (
            <tr key={row.code}>
              <td style={{ ...cell, ...left, color: "var(--ink-2)" }}>
                <Swatch color={row.color} label={row.label} />
              </td>
              <td style={cell}>{row.thickness.toFixed(1)}</td>
              <td style={cell}>{row.n.toLocaleString("en-US")}</td>
              <td style={cell}>{(row.share * 100).toFixed(1)}%</td>
              <td style={cell}>{num(row.phie_mean, 3)}</td>
              <td style={cell}>{num(row.rt_mean, 2)}</td>
            </tr>
          ))}
        </tbody>
      </Table>
      <span style={{ font: "400 10px/1.6 var(--font-sans)", color: "var(--ink-6)" }}>
        Thickness is the sample count times the median step, so it reads the
        same on a well logged every 0.1 m and one logged every 0.15 m. A flag
        is a reason to look again, not a reason to perforate.
      </span>
      {payload.published && (
        <Notice tone="info">
          Published as {payload.published.set} — {payload.published.note}
        </Notice>
      )}
    </div>
  );
}

/** Selang yang bersambung, dari yang paling tebal. */
function IntervalTable({ payload, classes }: {
  payload: LrlcPayload;
  classes: LrlcPayload["classes"];
}) {
  if (!payload.intervals.length) {
    return <Empty>No interval worth listing — nothing above water was flagged.</Empty>;
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
      <Table>
        <thead>
          <tr>
            <th style={{ ...head, ...left }}>Class</th>
            <th style={{ ...head, ...left }}>Well</th>
            <th style={head}>Top m</th>
            <th style={head}>Base m</th>
            <th style={head}>Thickness m</th>
            <th style={head}>Samples</th>
          </tr>
        </thead>
        <tbody>
          {payload.intervals.map((row, index) => (
            <tr key={`${row.well_id}-${row.code}-${row.top}-${index}`}>
              <td style={{ ...cell, ...left, color: "var(--ink-2)" }}>
                <Swatch color={colorOf(classes, row.code)} label={row.label} />
              </td>
              <td style={{ ...cell, ...left, color: "var(--ink-2)" }}>{row.well}</td>
              <td style={cell}>{row.top.toFixed(1)}</td>
              <td style={cell}>{row.base.toFixed(1)}</td>
              <td style={cell}>{row.thickness.toFixed(1)}</td>
              <td style={cell}>{row.samples.toLocaleString("en-US")}</td>
            </tr>
          ))}
        </tbody>
      </Table>
      <span style={{ font: "400 10px/1.6 var(--font-sans)", color: "var(--ink-6)" }}>
        Water and non-reservoir are left out: they fill most of a well, and what
        this list answers is which layers are worth a second look.
      </span>
    </div>
  );
}

/** Satu baris per sumur, berikut Rw yang benar-benar dipakainya. */
function WellTable({ payload, onOpenSheet }: {
  payload: LrlcPayload;
  /** Membuka lembar log sumur ini. Baris tabel adalah tempat sumur benar-benar
   *  dipilih orang, jadi di situlah pintunya — bukan hanya di toolbar, yang
   *  selalu membuka sumur pertama. */
  onOpenSheet: (wellId: number) => void;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
      <Table>
        <thead>
          <tr>
            <th style={{ ...head, ...left }}>Well</th>
            <th style={head}>Samples</th>
            <th style={head}>MD range</th>
            <th style={head}>Rw</th>
            <th style={head}>a · m</th>
            <th style={head}>Tests</th>
            <th style={head}>LRLC m</th>
            <th style={head}>Sheet</th>
          </tr>
        </thead>
        <tbody>
          {payload.wells.map((well) => (
            <tr key={well.id}>
              <td style={{ ...cell, ...left, color: "var(--ink-2)" }}>
                <Swatch color={well.color} label={well.name} />
              </td>
              <td style={cell}>{well.count.toLocaleString("en-US")}</td>
              <td style={cell}>
                {well.md_range[0].toFixed(0)}–{well.md_range[1].toFixed(0)}
              </td>
              <td style={cell}>{well.rw.toFixed(4)}</td>
              <td style={cell}>{well.a.toFixed(2)} · {well.m.toFixed(2)}</td>
              <td style={cell}>{well.tests ? well.tests.toLocaleString("en-US") : "—"}</td>
              <td style={{ ...cell, color: "var(--accent)" }}>
                {lrlcThickness(well.rows).toFixed(1)}
              </td>
              <td style={cell}>
                <button
                  type="button"
                  onClick={() => onOpenSheet(well.id)}
                  title={`Draw ${well.name} as a log sheet`}
                  style={{
                    border: "1px solid var(--border-control)",
                    borderRadius: 4, background: "var(--panel)",
                    color: "var(--ink-3)", cursor: "pointer",
                    font: "500 9.5px var(--font-mono)", padding: "1px 6px",
                  }}
                >
                  Open
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      {payload.skipped.length > 0 && (
        <Notice tone="warn">
          Left out, and why: {payload.skipped.map((s) => `${s.well} — ${s.reason}`).join("; ")}.
        </Notice>
      )}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 14 }}>
        <KeyValue k="Interval" v={payload.interval} />
        <KeyValue k="RT / RO cutoff" v={String(payload.cutoffs.rt_ro_cutoff)} />
        <KeyValue k="Smoothed below" v={`${payload.cutoffs.smooth_m} m`} />
      </div>
    </div>
  );
}
